import React, { useState, useEffect } from 'react'
import { Box, Tooltip, Pagination, Skeleton } from '@mui/material'
import { intelColors } from './intelTheme'

// Small ⓘ marker rendered after a column header when the column def carries `info`.
// Hover shows the calculation/formula so users understand how the value is derived.
const InfoDot = ({ text }) => (
  <Tooltip
    title={text}
    arrow
    placement='top'
    componentsProps={{
      tooltip: { sx: { fontSize: 11, fontWeight: 400, lineHeight: 1.45, maxWidth: 300, textTransform: 'none', letterSpacing: 0, p: '8px 10px' } },
    }}
  >
    <Box
      component='span'
      sx={{
        cursor: 'help',
        ml: '4px',
        width: 13,
        height: 13,
        flex: '0 0 auto',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '50%',
        border: `1px solid ${intelColors.primary}`,
        color: intelColors.primary,
        fontSize: 9,
        fontWeight: 700,
        lineHeight: 1,
        fontStyle: 'normal',
        textTransform: 'none',
        opacity: 0.65,
        '&:hover': { opacity: 1 },
      }}
    >
      i
    </Box>
  </Tooltip>
)

export default function IntelTable({
  columns, rows, title, action, loading, emptyMsg = 'No data', rowKey, expandable, renderRow, rowStyle, pageSize,
  // Server-side (controlled) pagination: pass serverSide + page (1-indexed) + totalRecords + onPageChange.
  // When serverSide is set, `rows` is treated as ALREADY being the current page (no client slicing).
  serverSide, page: pageProp, totalRecords, onPageChange,
}) {
  const [localPage, setLocalPage] = useState(0) // 0-indexed, client mode only
  const paginated = pageSize && pageSize > 0
  const rowCount = rows ? rows.length : 0
  const curPage1 = serverSide ? Math.max(1, pageProp || 1) : localPage + 1 // 1-indexed (display + control)
  const total = serverSide ? (typeof totalRecords === 'number' ? totalRecords : 0) : rowCount
  // Server mode: rows are already one page. Client mode: slice locally.
  const paged = serverSide
    ? (rows || [])
    : (paginated ? (rows || []).slice(localPage * pageSize, localPage * pageSize + pageSize) : (rows || []))
  const pageCount = paginated ? Math.max(1, Math.ceil(total / pageSize)) : 1
  const idxBase = (curPage1 - 1) * (paginated ? pageSize : 0)
  // Client mode: reset to first page when the row set or page size changes (e.g. after filtering).
  // Server mode: the parent owns the page, so never auto-reset here.
  useEffect(() => { if (!serverSide) setLocalPage(0) }, [rowCount, pageSize, serverSide])
  return (
    <Box
      sx={{
        background: '#fff',
        borderRadius: '8px',
        border: `1px solid ${intelColors.border}`,
        overflow: 'hidden',
        mb: '20px',
      }}
    >
      {(title || action) && (
        <Box
          sx={{
            padding: '14px 18px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid #f0f0f0',
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          <Box sx={{ fontSize: 13, fontWeight: 700, color: intelColors.text }}>{title}</Box>
          {action}
        </Box>
      )}
      <Box sx={{ overflowX: 'auto', position: 'relative' }}>
        <Box
          component='table'
          sx={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: 12,
            '& thead th': {
              background: intelColors.headRowBg,
              padding: '9px 12px',
              textAlign: 'left',
              fontSize: 11,
              fontWeight: 700,
              color: '#666',
              textTransform: 'uppercase',
              letterSpacing: '0.4px',
              borderBottom: `1px solid ${intelColors.border}`,
              whiteSpace: 'nowrap',
            },
            '& tbody td': {
              padding: '9px 12px',
              borderBottom: '1px solid #f4f4f4',
              color: '#333',
              verticalAlign: 'middle',
            },
            '& tbody tr:hover': { background: intelColors.rowHover },
            '& tbody tr:last-child td': { borderBottom: 'none' },
          }}
        >
          <thead>
            <tr>
              {columns.map((c) => (
                <th key={c.key} style={c.headerStyle || {}}>
                  <Box component='span' sx={{ display: 'inline-flex', alignItems: 'center' }}>
                    {c.header}
                    {c.info && <InfoDot text={c.info} />}
                  </Box>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading &&
              Array.from({ length: paginated ? Math.min(pageSize, 8) : 6 }).map((_, r) => (
                <tr key={`sk-${r}`}>
                  {columns.map((c) => (
                    <td key={c.key} style={c.cellStyle || {}}>
                      <Skeleton variant='text' animation='wave' sx={{ fontSize: 12, maxWidth: 140 }} />
                    </td>
                  ))}
                </tr>
              ))}
            {total === 0 && !loading && (
              <tr>
                <td colSpan={columns.length} style={{ textAlign: 'center', padding: '24px', color: intelColors.muted }}>
                  {emptyMsg}
                </td>
              </tr>
            )}
            {!loading && paged.map((row, i) => {
              const idx = idxBase + i
              if (renderRow) return renderRow(row, idx)
              const key = rowKey ? rowKey(row, idx) : idx
              return (
                <tr key={key} style={rowStyle ? rowStyle(row) : undefined}>
                  {columns.map((c) => (
                    <td key={c.key} style={c.cellStyle || {}}>
                      {c.render ? c.render(row, idx) : row[c.key]}
                    </td>
                  ))}
                </tr>
              )
            })}
          </tbody>
        </Box>
      </Box>
      {paginated && total > pageSize && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: '10px 16px', borderTop: '1px solid #f0f0f0', flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ fontSize: 11.5, color: intelColors.muted }}>
            Showing {(curPage1 - 1) * pageSize + 1}–{Math.min(curPage1 * pageSize, total)} of {formatCount(total)}
          </Box>
          <Pagination
            count={pageCount}
            page={curPage1}
            onChange={(_, p) => (serverSide ? onPageChange && onPageChange(p) : setLocalPage(p - 1))}
            size='small'
            color='primary'
          />
        </Box>
      )}
    </Box>
  )
}

function formatCount(n) {
  return typeof n === 'number' ? n.toLocaleString('en-IN') : n
}
